'use client';

import { useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';

/**
 * QR-код по готовому адресу.
 *
 * Отдельный компонент, а не кусок генератора QR: там вокруг кода живут
 * шаблоны, цвета и скачивание, и всё это здесь лишнее. Общая у них
 * только библиотека.
 *
 * Адрес приходит **параметром и кодируется целиком**. Соблазн взять его
 * из `window.location.href` здесь особенно велик, потому что страница
 * отправителя и есть страница передачи, но адрес в строке браузера
 * отличается от ссылки для получателя: в нём нет ни комнаты, ни ключа
 * после решётки. Код при этом нарисуется, и внешне всё будет выглядеть
 * правильно — получатель просто откроет страницу без ключа и увидит
 * «Ссылка неполная».
 *
 * Значение дублируется в `data-qr-value`: только так проверка может
 * сверить закодированное с показанной ссылкой. Прочитать содержимое
 * нарисованного canvas она не может.
 */
export function QrLink({ value, size = 160 }: { value: string; size?: number }) {
  const container = useRef<HTMLDivElement>(null);
  const qr = useRef<InstanceType<typeof QRCodeStyling> | null>(null);

  useEffect(() => {
    if (!value) return;

    const options: Record<string, unknown> = {
      width: size,
      height: size,
      data: value,
      type: 'canvas',
      margin: 8,
      // Уровень коррекции выше среднего: ссылка длинная (комната и ключ
      // дают под сотню знаков), рисунок получается плотным, а снимают его
      // с экрана под углом и с бликами.
      qrOptions: { typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'Q' },
      // Строгие квадраты и чистый чёрный: камера дешёвого телефона
      // распознаёт их надёжнее скруглённых точек.
      dotsOptions: { type: 'square', color: '#000000' },
      backgroundOptions: { color: '#ffffff' },
      cornersSquareOptions: { type: 'square' },
      cornersDotOptions: { type: 'square' },
    };

    if (!qr.current) {
      qr.current = new QRCodeStyling(options);
    } else {
      qr.current.update(options);
    }

    if (container.current) {
      container.current.innerHTML = '';
      qr.current.append(container.current);
    }
  }, [value, size]);

  return (
    <div
      ref={container}
      data-testid="qr-link"
      data-qr-value={value}
      // Белая подложка всегда, независимо от темы: в тёмной теме
      // прозрачный фон делает код нечитаемым для камеры.
      className="inline-block rounded-lg bg-white p-1 leading-none"
    />
  );
}
